import type { SettingsTabPermissions, UserPermissions } from '../types';

/** Chaves das áreas dentro do modal Configurações (alinhado ao menu lateral). */
export const SETTINGS_TAB_KEYS = [
    'roles',
    'columns',
    'users',
    'messaging',
    'costCenters',
    'postgres',
    'monday',
    'versions',
    'logs',
] as const;

export function createDefaultSettingsTabs(all: boolean): SettingsTabPermissions {
    return {
        roles: all,
        columns: all,
        users: all,
        messaging: all,
        costCenters: all,
        postgres: all,
        monday: all,
        versions: all,
        logs: all,
    };
}

/** Converte o que veio do banco em objeto sempre completo. */
export function normalizeSettingsTabsFromDb(
    perm_settings: boolean,
    detail: unknown,
): SettingsTabPermissions {
    const none = createDefaultSettingsTabs(false);
    if (!perm_settings) return none;

    if (detail == null || (typeof detail === 'object' && detail !== null && Object.keys(detail).length === 0)) {
        return createDefaultSettingsTabs(true);
    }

    const d = detail as Partial<SettingsTabPermissions>;
    return { ...none, ...d };
}

export function countEnabledSettingsTabs(tabs: SettingsTabPermissions): number {
    return SETTINGS_TAB_KEYS.filter((k) => tabs[k]).length;
}

export function hasAnySettingsTab(tabs: SettingsTabPermissions): boolean {
    return SETTINGS_TAB_KEYS.some((k) => tabs[k]);
}

/** Primeira aba permitida na ordem do menu. */
export function firstAllowedSettingsTab(tabs: SettingsTabPermissions): keyof SettingsTabPermissions | null {
    for (const k of SETTINGS_TAB_KEYS) {
        if (tabs[k]) return k;
    }
    return null;
}

export const MODULE_KEYS = ['dashboard', 'hr', 'settings', 'absences'] as const;
export type ModulePermissionKey = (typeof MODULE_KEYS)[number];

export function isModulePermissionKey(key: string): key is ModulePermissionKey {
    return (MODULE_KEYS as readonly string[]).includes(key);
}

/** Ações de CRUD de colaboradores no quadro Kanban. */
export const EMPLOYEE_ACTION_KEYS = ['employeeCreate', 'employeeEdit', 'employeeDelete'] as const;
export type EmployeeActionKey = (typeof EMPLOYEE_ACTION_KEYS)[number];

/**
 * Defaults de permissões de colaboradores para novos usuários.
 * @param full - true libera criar/editar/remover; false restringe criação e exclusão
 */
export function createDefaultEmployeePermissions(full: boolean): Pick<UserPermissions, EmployeeActionKey> {
    return {
        employeeCreate: full,
        employeeEdit: full,
        employeeDelete: full,
    };
}

/** Rótulo curto das ações de colaborador na aba Usuários. */
export function getEmployeeActionLabel(key: EmployeeActionKey): string {
    switch (key) {
        case 'employeeCreate':
            return 'Criar';
        case 'employeeEdit':
            return 'Editar';
        case 'employeeDelete':
            return 'Remover';
        default:
            return key;
    }
}

/** Indica se o usuário pode cadastrar colaboradores (fallback true para sessões legadas). */
export function canCreateEmployee(perms: UserPermissions | undefined): boolean {
    return perms?.employeeCreate !== false;
}

/** Indica se o usuário pode editar/mover colaboradores. */
export function canEditEmployee(perms: UserPermissions | undefined): boolean {
    return perms?.employeeEdit !== false;
}

/** Indica se o usuário pode excluir colaboradores. */
export function canDeleteEmployee(perms: UserPermissions | undefined): boolean {
    return perms?.employeeDelete !== false;
}
